import 'react-responsive-carousel/lib/styles/carousel.min.css';
import React, { FC, useCallback, useState, useMemo } from 'react';
import _orderBy from 'lodash/orderBy';
import { useGlobalNotification } from '@minecraft.notification-provider';
import CloudinaryMediaViewer from '@minecraft.cloudinary-media-viewer';
import MediaBin from '@minecraft.media-bin';
import Modal, { useModal } from '@minecraft.modal';
import { SCREEN_NAMES, AppType, MediaHelper } from '@blocs.common';
import { useBillingPopup, PurchaseResultType, PaywallLocation } from '@blocs.paywall';
import { MediaTypeCodeEnum, SearchMediaResultFragment } from '@minecraft.graphql-operations';
import { ImageCropperModal } from '@minecraft.image-cropper';
import { gqlProfileMedia, gqlMedia, gqlMediaTypeCodeEnum } from '@minecraft.graphql-types';
import { useTranslation } from '@blocs.i18n';
import { BodyText, FlexRow } from '@minecraft.atoms';
import { MediaUploader } from '../../MediaUploader';
import { MediaUploaderProps } from '../../MediaUploader/types';
import { captureExceptionEvent } from '../../Sentry';
import { MediaDisplay } from './MediaDisplay';
import { useProfileViewContext } from '../ProfileViewContext';
import RearrangeMedia from './RearrangeMedia';
import { CropperInstance, ReorderedMedia } from '../types';
import { MediaInfoWrap, InfoHeadingContainer, InfoHeading, StyledIcon, StyledLink, StyledDivider } from './styles';

const sectionName = 'media';

export const Media: FC & { RearrangeMedia?: typeof RearrangeMedia } = () => {
  const context = useProfileViewContext();
  const { showMessage } = useGlobalNotification();
  const { t } = useTranslation();
  const billingPopup = useBillingPopup();
  const [mediaUploaderContext, setMediaUploaderContext] = useState<{ mediaType: MediaTypeCodeEnum; maxFiles: number }>({
    mediaType: MediaTypeCodeEnum.Video,
    maxFiles: undefined,
  });
  const { appType, isPreview, fetcher, updater, showSummaryWithSubmissionDetails } = context;
  const {
    availableCreditsByType,
    entitlementsByType,
    audioMediaLibrary,
    isFolderCustomized,
    photoMediaLibrary,
    profile,
    submissionMedia,
    videoMediaLibrary,
  } = fetcher;
  const { addProfileMedias, mutationLoading, hideProfileMedia, rearrangeMedia, transformImage, transformThumbnail } =
    updater;

  const {
    isOpen: isMediaBinOpen,
    modalContext: mediaBinMediaType,
    close: closeMediaBin,
    open: openMediaBin,
  } = useModal<MediaTypeCodeEnum>();
  const {
    isOpen: isCarouselOpen,
    open: openCarousel,
    close: closeCarousel,
    modalContext: carouselContext,
  } = useModal<{ mediaIndex: number; mediaType: MediaTypeCodeEnum }>();
  const {
    isOpen: isRearrangeModalOpen,
    open: openRearrangeModal,
    close: closeRearrangeModal,
    modalContext: rearrangeContext,
  } = useModal<{ mediaType: MediaTypeCodeEnum }>();
  const {
    isOpen: isEditPhotoOpen,
    open: openEditPhotoModal,
    close: closeEditPhotoModal,
    modalContext: selectedPhotoToEdit,
  } = useModal<gqlMedia>();
  const [mediaBinSelectedIds, setMediaBinSelectedIds] = useState<number[]>([]);
  const [openSection, setOpenSection] = useState(true);
  const getMediaText = MediaHelper.useMediaText();

  const setMediaBinSelected = useCallback((mediaId: number) => {
    setMediaBinSelectedIds((currentValue) => {
      if (currentValue.includes(mediaId)) {
        return currentValue.filter((id) => id !== mediaId);
      }

      return [...currentValue, mediaId];
    });
  }, []);

  const openMediaViewer = (mediaIdx: number, selectedType: MediaTypeCodeEnum): void => {
    openCarousel({ mediaIndex: mediaIdx, mediaType: selectedType });
  };

  const sortedGroupedMediaAssets = useMemo(() => {
    const assetsToSort: gqlProfileMedia[] =
      showSummaryWithSubmissionDetails || isFolderCustomized ? submissionMedia : profile.mediaAssets?.concat();

    const groupedMediaList: Record<
      MediaTypeCodeEnum.Photo | MediaTypeCodeEnum.Video | MediaTypeCodeEnum.Audio,
      gqlProfileMedia[]
    > = {
      [MediaTypeCodeEnum.Photo]: [],
      [MediaTypeCodeEnum.Video]: [],
      [MediaTypeCodeEnum.Audio]: [],
    };

    const filteredAssets = assetsToSort.filter((m) => !m.media.isLocked);
    const sortedAssets = _orderBy(
      filteredAssets,
      [(m) => m.order, (m) => !m.isPrimary, (m) => m.created],
      ['asc', 'desc']
    );

    sortedAssets.forEach((mediaAsset) => {
      if (groupedMediaList?.[mediaAsset?.media?.mediaType?.code]) {
        groupedMediaList[mediaAsset?.media?.mediaType?.code]?.push(mediaAsset);
      }
    });

    return groupedMediaList;
  }, [isFolderCustomized, profile, showSummaryWithSubmissionDetails, submissionMedia]);

  const profileMediaIds = useMemo(() => {
    return profile.mediaAssets?.map((media) => media?.media?.mediaId);
  }, [profile]);
  const mediaLibraryForTypes = useMemo(() => {
    return {
      [MediaTypeCodeEnum.Photo]: photoMediaLibrary.filter((m) => !profileMediaIds?.includes(m.mediaId)),
      [MediaTypeCodeEnum.Video]: videoMediaLibrary.filter((m) => !profileMediaIds?.includes(m.mediaId)),
      [MediaTypeCodeEnum.Audio]: audioMediaLibrary.filter((m) => !profileMediaIds?.includes(m.mediaId)),
    };
  }, [photoMediaLibrary, audioMediaLibrary, videoMediaLibrary, profileMediaIds]);
  const attachMediasToProfile = useCallback(async () => {
    if (!mediaBinSelectedIds?.length) {
      closeMediaBin();
      return;
    }

    const medias = mediaLibraryForTypes?.[mediaBinMediaType]?.filter((m: SearchMediaResultFragment) =>
      mediaBinSelectedIds.includes(m.mediaId)
    );
    await addProfileMedias({ medias });
  }, [addProfileMedias, closeMediaBin, mediaBinMediaType, mediaBinSelectedIds, mediaLibraryForTypes]);

  const isReps = appType === AppType.Reps;
  const isTalent = appType === AppType.Talent;

  const handleMediaUpload: MediaUploaderProps['onCloseUploader'] = useCallback(
    async (succeededUploads) => {
      if (!succeededUploads?.length) {
        return;
      }

      try {
        let shouldAttach = false;
        let includeLocked = false;

        const { lockedMedia, unlockedMedia } = succeededUploads.reduce(
          (acc, media) => {
            if (media.isLocked) {
              acc.lockedMedia.push(media);
            } else {
              acc.unlockedMedia.push(media);
            }

            return acc;
          },
          {
            lockedMedia: [],
            unlockedMedia: [],
          }
        );

        if (isReps && lockedMedia?.length) {
          // show alert message and bail
          // this should only be possible if someone in another
          // session uploaded and consumed a credit since this session started
          // b/c the upload button is disabled when the user has no credits
          showMessage({ color: 'danger', message: t('common:profile.mediaBank.attachMediaFailed') });
          await updater.refetch({ refetchProfile: true, refetchMedia: true });
        }

        if (isTalent && lockedMedia?.length) {
          const { getPurchaseResult } = billingPopup.show({
            location: PaywallLocation.PROFILE_MEDIA,
            mediaIds: lockedMedia.map((m) => m.mediaId),
          });
          const paywallResult = await getPurchaseResult();
          if (paywallResult?.type === PurchaseResultType.Success) {
            shouldAttach = true;
            includeLocked = true;
          }
        }

        if (unlockedMedia?.length) {
          shouldAttach = true;
        }

        if (shouldAttach) {
          const mediaToAttach = includeLocked ? succeededUploads : unlockedMedia;
          await updater.addProfileMedias({ medias: mediaToAttach });
        }
      } catch (e) {
        captureExceptionEvent(e);
        showMessage({
          color: 'danger',
          message: t('common:profile.mediaBank.attachMediaFailed'),
        });
      }
    },
    [updater, billingPopup, showMessage, t, isReps, isTalent]
  );
  const handleEditSubmit = useCallback(
    async ({ isThumbnail, cropper }: { isThumbnail: boolean; cropper: CropperInstance }) => {
      if (isThumbnail) {
        await transformThumbnail({ media: selectedPhotoToEdit, cropper });
      } else {
        await transformImage({ media: selectedPhotoToEdit, cropper });
      }

      closeEditPhotoModal();
    },
    [transformThumbnail, transformImage, selectedPhotoToEdit, closeEditPhotoModal]
  );
  const handleRearrangeMediaUpdate = useCallback(
    async (profileMediaList: ReorderedMedia[]) => {
      await rearrangeMedia({ profileMediaList, mediaType: rearrangeContext?.mediaType });
      closeRearrangeModal();
    },
    [rearrangeMedia, rearrangeContext?.mediaType, closeRearrangeModal]
  );
  const { maxFilesByType, uploadAllowedByType } = useMemo(() => {
    const photoCreditsRemaining = availableCreditsByType[gqlMediaTypeCodeEnum.PHOTO];
    const videoCreditsRemaining = availableCreditsByType[gqlMediaTypeCodeEnum.VIDEO];
    const audioCreditsRemaining = availableCreditsByType[gqlMediaTypeCodeEnum.AUDIO];

    return {
      maxFilesByType: {
        [MediaTypeCodeEnum.Photo]: !Number.isFinite(entitlementsByType[gqlMediaTypeCodeEnum.PHOTO])
          ? undefined
          : photoCreditsRemaining,
        [MediaTypeCodeEnum.Video]: !Number.isFinite(entitlementsByType[gqlMediaTypeCodeEnum.VIDEO])
          ? undefined
          : videoCreditsRemaining,
        [MediaTypeCodeEnum.Audio]: !Number.isFinite(entitlementsByType[gqlMediaTypeCodeEnum.AUDIO])
          ? undefined
          : audioCreditsRemaining,
      },
      uploadAllowedByType: {
        [MediaTypeCodeEnum.Photo]:
          !Number.isFinite(entitlementsByType[gqlMediaTypeCodeEnum.PHOTO]) || photoCreditsRemaining > 0,
        [MediaTypeCodeEnum.Video]:
          !Number.isFinite(entitlementsByType[gqlMediaTypeCodeEnum.VIDEO]) || videoCreditsRemaining > 0,
        [MediaTypeCodeEnum.Audio]:
          !Number.isFinite(entitlementsByType[gqlMediaTypeCodeEnum.AUDIO]) || audioCreditsRemaining > 0,
      },
    };
  }, [availableCreditsByType, entitlementsByType]);

  if (
    isPreview &&
    !sortedGroupedMediaAssets?.[MediaTypeCodeEnum.Photo]?.length &&
    !sortedGroupedMediaAssets?.[MediaTypeCodeEnum.Video]?.length &&
    !sortedGroupedMediaAssets?.[MediaTypeCodeEnum.Audio]?.length
  ) {
    return null;
  }

  return (
    <MediaUploader
      artistId={Number(fetcher?.artistId)}
      availableCreditsByType={availableCreditsByType}
      entitlementsByType={entitlementsByType}
      mediaType={mediaUploaderContext?.mediaType}
      openModalButtonText={t('common:profile.button.uploadNew')}
      onCloseUploader={handleMediaUpload}
      maxFiles={isReps ? mediaUploaderContext?.maxFiles : undefined}
      renderChildren={({ toggleModal: toggleMediaUploader }) => (
        <div id="div-media" data-testid="profile-media-container">
          <MediaInfoWrap>
            <InfoHeadingContainer>
              <InfoHeading isPreview>{t('common:profile.nav.media')}</InfoHeading>
              {isReps && !isPreview && (
                <>
                  <StyledDivider />
                  <StyledLink to={`/profile/${profile.profileId}/artist/${profile.artistId}/media`}>
                    {t('common:profile.seeAllTalentMedia')}
                  </StyledLink>
                </>
              )}
              {isPreview && (
                <StyledIcon
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-${
                    openSection ? 'chevron-open-thick' : 'chevron-closed-thick'
                  }-icon`}
                  name={openSection ? 'chevron-open-thick' : 'chevron-closed-thick'}
                  onClick={(): void => setOpenSection(!openSection)}
                />
              )}
            </InfoHeadingContainer>
            {isReps && !isPreview && Number.isFinite(entitlementsByType[gqlMediaTypeCodeEnum.PHOTO]) && (
              <FlexRow
                my="4"
                w="full"
                borderPlacement="all"
                className="cn_atom_p-4"
                alignItems="center"
                justifyContent="center"
                data-testid="profile-media-credits-info-banner"
              >
                <BodyText align="center" textWrap="pretty" className="cn_w-full">
                  {t('common:profileMedia.creditsInfoBanner.ln1')}
                </BodyText>
                <BodyText align="center" textWrap="pretty" className="cn_w-full">
                  {t('common:profileMedia.creditsInfoBanner.ln2')}
                </BodyText>
              </FlexRow>
            )}
            {openSection &&
              Object.keys(sortedGroupedMediaAssets).map((mediaAssetType: MediaTypeCodeEnum) => (
                <MediaDisplay
                  key={mediaAssetType}
                  mediaType={mediaAssetType}
                  mediaList={sortedGroupedMediaAssets?.[mediaAssetType]}
                  openMediaBin={() => openMediaBin(mediaAssetType)}
                  openMediaUploader={() => {
                    setMediaUploaderContext({ mediaType: mediaAssetType, maxFiles: maxFilesByType?.[mediaAssetType] });
                    toggleMediaUploader();
                  }}
                  openMediaViewer={openMediaViewer}
                  uploadNewAllowed={isTalent || uploadAllowedByType?.[mediaAssetType]}
                  removeProfileMedia={({ mediaId }) => hideProfileMedia({ mediaId })}
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}`}
                  openPhotoEditor={(media) => openEditPhotoModal(media)}
                  openRearrangeModal={() => openRearrangeModal({ mediaType: mediaAssetType })}
                />
              ))}
            {isCarouselOpen && (
              <CloudinaryMediaViewer
                isSmall
                mediaList={sortedGroupedMediaAssets?.[carouselContext?.mediaType]}
                stageName={profile?.stageName}
                startIndex={carouselContext?.mediaIndex}
                handleCloseModal={() => closeCarousel()}
              />
            )}
            {isMediaBinOpen && (
              <Modal
                isOpen
                title={isReps ? t('common:profile.talentMedia') : t('common:profile.mediaBank')}
                handleToggleClick={() => closeMediaBin()}
                submitLabel={mediaBinSelectedIds.length ? t('common:button.save') : t('common:button.cancel')}
                handleSubmitClick={() => attachMediasToProfile()}
                data-testid="media-bin-modal"
              >
                <MediaBin
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}`}
                  selectedMediaIds={mediaBinSelectedIds}
                  mediaTypes={[mediaBinMediaType]}
                  onChange={({ mediaId }) => setMediaBinSelected(mediaId)}
                  medias={mediaLibraryForTypes?.[mediaBinMediaType]}
                  isSingleSelection={false}
                />
              </Modal>
            )}
            {isRearrangeModalOpen && (
              <Modal
                isOpen
                title={t('common:profile.rearrangeProfileMedia', {
                  mediaType: getMediaText(rearrangeContext?.mediaType, 'pluralTitle'),
                })}
                handleToggleClick={() => closeRearrangeModal()}
                isFooterHidden
              >
                <RearrangeMedia
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-rearrange`}
                  mediaType={rearrangeContext?.mediaType}
                  mediaAssets={sortedGroupedMediaAssets?.[rearrangeContext?.mediaType]}
                  handleToggleClick={() => closeRearrangeModal()}
                  handleSubmitClick={handleRearrangeMediaUpdate}
                />
              </Modal>
            )}
            {isEditPhotoOpen && (
              <ImageCropperModal
                isOpen
                handleClose={() => closeEditPhotoModal()}
                handleSave={(cropper) => handleEditSubmit({ isThumbnail: false, cropper })}
                disableSubmit={mutationLoading}
                media={selectedPhotoToEdit}
                handleThumbnailSave={(cropper) => handleEditSubmit({ isThumbnail: true, cropper })}
              />
            )}
          </MediaInfoWrap>
        </div>
      )}
    />
  );
};

Media.RearrangeMedia = RearrangeMedia;
